import { toast } from 'react-toastify';
import type { ToastOptions } from 'react-toastify';
import type { JSX } from 'react';

const defaultOptions: ToastOptions = {
  position: 'top-center',
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: true,
  progress: undefined,
  pauseOnFocusLoss: false,
};

const stylePresets = {
  SMALL: {
    style: {
      minHeight: '40px',
      fontSize: '16px',
      padding: '8px 12px',
      width: '240px',
    },
  },
  MEDIUM: {
    style: {
      minHeight: '60px',
      fontSize: '16px',
      padding: '12px 16px',
      width: '360px',
    },
  },
  LARGE: {
    style: {
      minHeight: '80px',
      fontSize: '16px',
      padding: '16px 20px',
      width: '480px',
    },
  },
  XLARGE: {
    style: {
      minHeight: '80px',
      fontSize: '16px',
      padding: '16px 20px',
      width: '600px',
    },
  },
} as const;

type StylePreset = keyof typeof stylePresets;

const TOAST_IDS = {
  info: 'singleton-toast-info',
  success: 'singleton-toast-success',
  warning: 'singleton-toast-warning',
  error: 'singleton-toast-error',
};

const resolveOptions = (options?: ToastOptions | StylePreset): ToastOptions => {
  if (options === undefined) {
    return { ...defaultOptions, ...stylePresets.LARGE };
  }

  if (typeof options === 'string') {
    return { ...defaultOptions, ...stylePresets[options] };
  }
  return { ...defaultOptions, ...options };
};

const formatMessageWithLineBreaks = (message: string) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      paddingLeft: '0.5rem',
    }}
  >
    {message.split('\n').map((line, index) => (
      <span key={index + '-' + 'line'}>
        {line}
        <br />
      </span>
    ))}
  </div>
);

const resolveMessage = (message: string | JSX.Element): string | JSX.Element | JSX.Element[] =>
  typeof message === 'string' && message.includes('\n') ? (
    formatMessageWithLineBreaks(message)
  ) : (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        paddingLeft: '0.5rem',
      }}
    >
      <span>{message}</span>
    </div>
  );

export const info = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  toast.info(resolveMessage(message), resolveOptions(options));
};

export const success = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  toast.success(resolveMessage(message), resolveOptions(options));
};

export const warning = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  toast.warning(resolveMessage(message), resolveOptions(options));
};

export const error = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  toast.error(resolveMessage(message), resolveOptions(options));
};

export const infoOnce = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  if (!toast.isActive(TOAST_IDS.info)) {
    toast.info(resolveMessage(message), {
      toastId: TOAST_IDS.info,
      ...resolveOptions(options),
    });
  }
};

export const successOnce = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  if (!toast.isActive(TOAST_IDS.success)) {
    toast.success(resolveMessage(message), {
      toastId: TOAST_IDS.success,
      ...resolveOptions(options),
    });
  }
};

export const warningOnce = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  if (!toast.isActive(TOAST_IDS.warning)) {
    toast.warning(resolveMessage(message), {
      toastId: TOAST_IDS.warning,
      ...resolveOptions(options),
    });
  }
};

export const errorOnce = (message: string | JSX.Element, options?: ToastOptions | StylePreset) => {
  if (!toast.isActive(TOAST_IDS.error)) {
    toast.error(resolveMessage(message), {
      toastId: TOAST_IDS.error,
      ...resolveOptions(options),
    });
  }
};

export const toastStyle = stylePresets;
